import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wallet, Check, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import WalletConnectModal from '@/components/WalletConnectModal';
import { toast } from 'sonner';

const demoPresets = [
  { label: 'Donor', type: 'donor' },
  { label: 'NGO Admin', type: 'ngo' },
  { label: 'Volunteer', type: 'volunteer' },
  { label: 'Partner', type: 'corporate' },
];

export default function SignInPage() {
  const navigate = useNavigate();
  const { login, loginAsType } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    login({ name: email.split('@')[0], email, type: 'donor', username: email.split('@')[0].toLowerCase().replace(/\s+/g, '-'), walletAddress: '' });
    navigate('/dashboard');
  };

  const handleDemoLogin = (type) => {
    loginAsType(type);
    navigate('/dashboard');
  };

  const handleForgotPassword = () => {
    toast.success('Password reset link sent to your email', { description: 'Check your inbox for instructions.' });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center py-12 px-4" data-testid="signin-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#111827] tracking-tight mb-2">Welcome Back</h1>
          <p className="text-[#4B5563] font-body">Sign in to continue your impact journey</p>
        </div>

        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-xl border-gray-200 font-body" required data-testid="signin-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Password</label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="rounded-xl border-gray-200 font-body pr-10" required data-testid="signin-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={handleForgotPassword} className="text-xs text-[#7C3AED] hover:underline font-body" data-testid="forgot-password-link">Forgot password?</button>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white rounded-full h-11 font-medium border-0 hover:shadow-lg transition-all" data-testid="signin-submit-btn">
              Sign In
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-4 text-xs text-gray-400 font-body">or continue with</span></div>
          </div>

          <div className="space-y-2">
            <Button variant="outline" onClick={() => toast.info('Google SSO coming soon', { description: 'Use email login or demo presets for now.' })} className="w-full rounded-full h-11 border-gray-200 font-body text-sm gap-2" data-testid="sso-google">
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </Button>
            <Button variant="outline" onClick={() => toast.info('Apple SSO coming soon', { description: 'Use email login or demo presets for now.' })} className="w-full rounded-full h-11 border-gray-200 font-body text-sm gap-2" data-testid="sso-apple">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-1.55 4.31-3.74 4.25z"/></svg>
              Continue with Apple
            </Button>
            <Button variant="outline" onClick={() => setWalletOpen(true)} className={`w-full rounded-full h-11 border-gray-200 font-body text-sm gap-2 ${walletConnected ? 'border-[#10B981] text-[#10B981]' : ''}`} data-testid="wallet-connect-btn">
              <Wallet className="w-4 h-4" />
              {walletConnected ? 'Wallet Connected' : 'Connect Wallet'}
              {walletConnected && <Check className="w-4 h-4" />}
            </Button>
          </div>

          <div className="mt-6 pt-5 border-t border-gray-100">
            <p className="text-xs text-gray-400 text-center font-body mb-3">Quick demo login</p>
            <div className="flex items-center justify-center gap-1 flex-wrap">
              {demoPresets.map(p => (
                <button key={p.type} onClick={() => handleDemoLogin(p.type)} className="text-xs text-[#7C3AED] hover:text-[#2563EB] font-medium font-body px-2 py-1 rounded-lg hover:bg-[#7C3AED]/5 transition-colors" data-testid={`demo-${p.type}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="text-center mt-6 text-sm text-gray-500 font-body">
          Don't have an account? <Link to="/signup" className="text-[#7C3AED] font-medium hover:underline" data-testid="goto-signup">Join HumaClub</Link>
        </p>
      </motion.div>

      <WalletConnectModal open={walletOpen} onOpenChange={setWalletOpen} onConnected={() => setWalletConnected(true)} />
    </div>
  );
}
